// src/components/ConfirmModal.js
// Reusable confirmation modal for destructive or important actions
import React from "react";
import { useTheme } from "../context/ThemeContext";
import { useTranslation } from "react-i18next";

/**
 * @param {object} props
 * @param {boolean} props.open - Whether modal is visible
 * @param {() => void} props.onClose - Called when user cancels
 * @param {() => void} props.onConfirm - Called when user confirms
 * @param {string} props.title - Modal title
 * @param {string} props.message - Modal message/description
 * @param {string} [props.confirmLabel] - Confirm button text (default: "Confirm")
 * @param {string} [props.cancelLabel] - Cancel button text (default: "Cancel")
 * @param {boolean} [props.destructive] - If true, confirm button is red/warning style
 */
export default function ConfirmModal({
    open,
    onClose,
    onConfirm,
    title,
    message,
    confirmLabel,
    cancelLabel,
    destructive = false,
}) {
    const { isDark, isForest } = useTheme();
    const { t: tr } = useTranslation();

    if (!open) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const confirmText = confirmLabel || tr("common.confirm") || "Confirm";
    const cancelText = cancelLabel || tr("common.cancel") || "Cancel";

    // Theme-aware colors
    const bgColor = isDark ? "#1e293b" : isForest ? "#1a3c34" : "#fff";
    const textColor = isDark ? "#f1f5f9" : isForest ? "#f0fdf4" : "#1e293b";
    const secondaryText = isDark ? "#94a3b8" : isForest ? "#a7f3d0" : "#64748b";
    const borderColor = isDark ? "#334155" : isForest ? "#065f46" : "#e2e8f0";

    // Destructive button colors
    const confirmBg = destructive
        ? "#dc2626"
        : isForest ? "#059669" : isDark ? "#3b82f6" : "#2563eb";
    const confirmHoverBg = destructive
        ? "#b91c1c"
        : isForest ? "#047857" : isDark ? "#2563eb" : "#1d4ed8";

    return (
        <div
            onClick={handleBackdropClick}
            style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(0,0,0,0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 9999,
                backdropFilter: "blur(4px)",
            }}
        >
            <div
                style={{
                    backgroundColor: bgColor,
                    borderRadius: 16,
                    padding: 24,
                    maxWidth: 400,
                    width: "90%",
                    boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
                    border: `1px solid ${borderColor}`,
                }}
            >
                {/* Title */}
                <h2
                    style={{
                        margin: 0,
                        marginBottom: 12,
                        fontSize: 18,
                        fontWeight: 600,
                        color: destructive ? "#dc2626" : textColor,
                    }}
                >
                    {title}
                </h2>

                {/* Message */}
                <p
                    style={{
                        margin: 0,
                        marginBottom: 24,
                        fontSize: 14,
                        color: secondaryText,
                        lineHeight: 1.5,
                    }}
                >
                    {message}
                </p>

                {/* Buttons */}
                <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                    <button
                        onClick={onClose}
                        style={{
                            padding: "10px 20px",
                            borderRadius: 8,
                            border: `1px solid ${borderColor}`,
                            backgroundColor: "transparent",
                            color: textColor,
                            fontSize: 14,
                            fontWeight: 500,
                            cursor: "pointer",
                            transition: "all 0.2s",
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = isDark ? "#334155" : isForest ? "#065f46" : "#f1f5f9";
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                        }}
                    >
                        {cancelText}
                    </button>
                    <button
                        onClick={() => {
                            onConfirm();
                            onClose();
                        }}
                        style={{
                            padding: "10px 20px",
                            borderRadius: 8,
                            border: "none",
                            backgroundColor: confirmBg,
                            color: "#fff",
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: "pointer",
                            transition: "all 0.2s",
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = confirmHoverBg;
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = confirmBg;
                        }}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
}
